"use client";

import Link from "@/app/[lang]/_components/Link";
import { createT } from "@/app/[lang]/_utils/create-t";
import Google from "@/assets/components/Google";
import type { Lang } from "@/langs";
import { authClient } from "@/lib/auth-client";
import { Button } from "@borderless/ui/components/button";
import BorderlessLong from "@borderless/ui/icons/BorderlessLong";
import { Roboto } from "next/font/google";
import { usePathname, useSearchParams } from "next/navigation";
import { Suspense, useState } from "react";
import { useFormStatus } from "react-dom";
import { toast } from "@borderless/ui/components/sonner";
import dictionary from "./t.json";
import type { AuthFormProps, AuthType } from "./types";

const t = createT(dictionary);

export function AuthForm(props: AuthFormProps) {
  return (
    <Suspense fallback={null}>
      <AuthFormContent {...props} />
    </Suspense>
  );
}

function AuthFormContent(props: AuthFormProps) {
  const pathname = usePathname();
  const params = useSearchParams();
  const callbackUrlEncoded = params.get("callbackUrl");

  const [authType, setAuthType] = useState<AuthType>(props.type);
  const callbackUrl =
    props.pageType === "modal"
      ? `${pathname}?${params.toString()}`
      : callbackUrlEncoded !== null
        ? decodeURIComponent(callbackUrlEncoded)
        : null;

  const [isLoading, setIsLoading] = useState(false);
  const [emailInput, setEmailInput] = useState<string | null>(null);
  const [emailSentTo, setEmailSentTo] = useState<string | null>(null);

  if (emailSentTo) {
    return (
      <div>
        <h1 className="flex items-center gap-2 text-4xl">
          {t("Check your Inbox", props.lang)}
        </h1>
        <div className="h-4" />
        <p className="text-lg">
          {t("We sent an email to", props.lang)}
          <br />
          <strong>{emailSentTo}</strong> <br />
          {t("Enter your code below:", props.lang)}
        </p>
        <div className="h-4" />
        <AuthCodeSignin
          email={emailSentTo}
          callbackUrl={callbackUrl}
          type={authType}
          lang={props.lang}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="h-(--header-height) relative flex w-full items-end justify-start">
        <BorderlessLong className="absolute bottom-0 left-1/2 mx-auto h-6 -translate-x-1/2" />
      </div>
      <div className="relative flex w-full flex-col justify-center text-center">
        <div className="space-y-8 px-3 py-8 md:space-y-16">
          <Image
            src="/images/onboarding/veronica-welcome.webp"
            width={144}
            height={144}
            alt="Veronica greeting you"
            className="mx-auto aspect-square max-w-36 lg:hidden"
            priority
          />
          {authType === "login" ? (
            <h1 className="font-(family-name:--font-caprasimo) text-3xl">
              {props.loginText ?? t("Welcome Back!", props.lang)}
            </h1>
          ) : (
            <h1 className="font-(family-name:--font-caprasimo) text-center text-3xl">
              {props.signupText ?? t("Welcome to Borderless!", props.lang)}
            </h1>
          )}
          <div className="flex flex-col items-stretch justify-center gap-3">
            {!props.isInsecureInAppBrowser && (
              <>
                <SignInButtonGoogle
                  authType={authType}
                  lang={props.lang}
                  callbackUrl={callbackUrl}
                />
                <hr className="border-border-secondary my-4 w-full border-t" />
              </>
            )}
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setIsLoading(true);

                if (!emailInput) {
                  toast.error("Please enter your email");
                  setIsLoading(false);
                  return;
                }

                const result = await authClient.emailOtp.sendVerificationOtp({
                  email: emailInput,
                  type: "sign-in",
                });

                if (result.error) {
                  toast.error(result.error.message ?? "Something went wrong");
                  setIsLoading(false);
                  return;
                }

                setEmailSentTo(emailInput);
              }}
              className="flex flex-col items-stretch justify-center gap-5"
              tabIndex={-1}
            >
              <Input
                type="email"
                name="email"
                onChange={(e) => {
                  setEmailInput(e.target.value);
                }}
                value={emailInput ?? ""}
                placeholder={t("Your Email", props.lang, true)}
              />
              <SignInButtonEmail
                buttonText={t("Continue", props.lang, true)}
                isLoading={isLoading}
              />
            </form>

            <div className="space-y-2">
              <span className="text-subtle text-sm">
                {t(
                  "By signing up I agree to [terms of use](/terms-of-use) and [privacy policy](/privacy-policy)",
                  props.lang,
                )}
              </span>
              {authType === "login" ? (
                <p className="text-subtle text-sm">
                  <span>{t("Don’t have an account?", props.lang)}</span>{" "}
                  {props.pageType === "modal" ? (
                    <Button
                      variant="link"
                      onClick={() => {
                        setAuthType("signup");
                      }}
                    >
                      {t("Sign Up for Free", props.lang)}
                    </Button>
                  ) : (
                    <Link
                      href={`/signup?${params.toString()}`}
                      className="underline"
                    >
                      {t("Sign Up for Free", props.lang)}
                    </Link>
                  )}
                </p>
              ) : (
                <p className="text-subtle text-sm">
                  <span>{t("Already have an account?", props.lang)}</span>{" "}
                  {props.pageType === "modal" ? (
                    <Button
                      variant="link"
                      onClick={() => {
                        setAuthType("login");
                      }}
                    >
                      {t("Log In", props.lang)}
                    </Button>
                  ) : (
                    <Link
                      href={`/login?${params.toString()}`}
                      className="underline"
                    >
                      {t("Log In", props.lang)}
                    </Link>
                  )}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

import Image from "@/components/Image";
import { Input } from "@borderless/ui/components/input";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@borderless/ui/components/input-otp";
import { Spinner } from "@borderless/ui/components/spinner";

const AuthCodeSignin = ({
  email,
  callbackUrl,
  type,
  lang,
}: {
  email: string;
  callbackUrl: string | null;
  type: "login" | "signup";
  lang: Lang;
}) => {
  const buttonText =
    type === "login" ? t("Log In", lang, true) : t("Sign Up", lang, true);

  const otpLength = 6;
  const [isLoading, setIsLoading] = useState(false);
  const [otpInput, setOtpInput] = useState<string | null>(null);

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        setIsLoading(true);

        if (!otpInput || otpInput.length !== otpLength) {
          toast.error("Please enter your OTP");
          setIsLoading(false);
          return;
        }

        const result = await authClient.signIn.emailOtp({
          email,
          otp: otpInput,
        });

        if (result.error) {
          setIsLoading(false);
          toast.error(result.error.message ?? "Something went wrong");
          return;
        }

        window.location.href = callbackUrl ?? "/";
      }}
    >
      <div>
        <InputOTP
          maxLength={otpLength}
          name="otp"
          onChange={(value) => {
            setOtpInput(value);
          }}
          value={otpInput ?? ""}
        >
          {Array.from({ length: 6 }).map((_, index) => (
            <InputOTPGroup key={`otp-${index}`}>
              <InputOTPSlot index={index} />
            </InputOTPGroup>
          ))}
        </InputOTP>
      </div>
      <div className="h-4" />
      <SignInButtonEmail buttonText={buttonText} isLoading={isLoading} />
    </form>
  );
};

function SignInButtonEmail(props: { buttonText: string; isLoading: boolean }) {
  const { pending } = useFormStatus();
  return (
    <Button variant="default" type="submit">
      {pending || props.isLoading ? <Spinner /> : props.buttonText}
    </Button>
  );
}

const roboto = Roboto({
  weight: ["500"],
  subsets: ["latin"],
});

function SignInButtonGoogle(props: {
  authType: AuthType;
  lang: Lang;
  callbackUrl: string | null;
}) {
  const [isLoading, setIsLoading] = useState(false);

  return (
    <Button
      onClick={async () => {
        setIsLoading(true);
        const result = await authClient.signIn.social({
          provider: "google",
          callbackURL: props.callbackUrl ?? undefined,
        });

        if (result.error) {
          toast.error(result.error.message ?? "Something went wrong");
          setIsLoading(false);
          return;
        }
      }}
      variant="outline"
      type="submit"
      className="w-full gap-1 ps-2"
    >
      {isLoading ? (
        <Spinner />
      ) : (
        <>
          <Google className="size-5" />
          <span className={`${roboto.className}`}>
            {props.authType === "signup"
              ? t("Sign Up with Google", props.lang)
              : t("Login with Google", props.lang)}
          </span>
        </>
      )}
    </Button>
  );
}
